export const gbaInputActions = [
  'A',
  'B',
  'L',
  'R',
  'SELECT',
  'START',
  'UP',
  'DOWN',
  'LEFT',
  'RIGHT',
] as const;

export type GbaInputAction = (typeof gbaInputActions)[number];

export const emulatorJsKeyboardKeys = [
  'z',
  'x',
  'a',
  's',
  'q',
  'e',
  'w',
  'd',
  'v',
  'enter',
  'space',
  'shift',
  'ctrl',
  'tab',
  'up arrow',
  'down arrow',
  'left arrow',
  'right arrow',
] as const;

export type EmulatorJsKeyboardKey = (typeof emulatorJsKeyboardKeys)[number];

export const emulatorJsGamepadBindings = [
  'BUTTON_1',
  'BUTTON_2',
  'BUTTON_3',
  'BUTTON_4',
  'SELECT',
  'START',
  'DPAD_UP',
  'DPAD_DOWN',
  'DPAD_LEFT',
  'DPAD_RIGHT',
  'LEFT_TOP_SHOULDER',
  'RIGHT_TOP_SHOULDER',
  'LEFT_BOTTOM_SHOULDER',
  'RIGHT_BOTTOM_SHOULDER',
] as const;

export type EmulatorJsGamepadBinding = (typeof emulatorJsGamepadBindings)[number];

export interface GbaInputProfile {
  readonly keyboard: Readonly<Record<GbaInputAction, EmulatorJsKeyboardKey>>;
  readonly gamepad: Readonly<Record<GbaInputAction, EmulatorJsGamepadBinding>>;
}

export interface EmulatorJsControllerBinding {
  readonly value?: string;
  readonly value2?: string;
}

export type EmulatorJsDefaultControllers = Readonly<
  Record<number, Readonly<Record<number, EmulatorJsControllerBinding>>>
>;

export const defaultGbaInputProfile: GbaInputProfile = {
  keyboard: {
    A: 'z',
    B: 'x',
    L: 'q',
    R: 'e',
    SELECT: 'v',
    START: 'enter',
    UP: 'up arrow',
    DOWN: 'down arrow',
    LEFT: 'left arrow',
    RIGHT: 'right arrow',
  },
  gamepad: {
    A: 'BUTTON_1',
    B: 'BUTTON_2',
    L: 'LEFT_TOP_SHOULDER',
    R: 'RIGHT_TOP_SHOULDER',
    SELECT: 'SELECT',
    START: 'START',
    UP: 'DPAD_UP',
    DOWN: 'DPAD_DOWN',
    LEFT: 'DPAD_LEFT',
    RIGHT: 'DPAD_RIGHT',
  },
};

export const gbaEmulatorJsButtonIds: Readonly<Record<GbaInputAction, number>> = {
  A: 8,
  B: 0,
  L: 10,
  R: 11,
  SELECT: 2,
  START: 3,
  UP: 4,
  DOWN: 5,
  LEFT: 6,
  RIGHT: 7,
};

function assertUnique(values: readonly string[], label: string): void {
  if (new Set(values).size !== values.length) {
    throw new Error(`INPUT_PROFILE_CONFLICT: duplicate ${label} binding`);
  }
}

export function validateGbaInputProfile(profile: GbaInputProfile): GbaInputProfile {
  const keyboardKeys = new Set<string>(emulatorJsKeyboardKeys);
  const gamepadBindings = new Set<string>(emulatorJsGamepadBindings);

  for (const action of gbaInputActions) {
    if (!keyboardKeys.has(profile.keyboard[action])) {
      throw new Error(`INPUT_PROFILE_INVALID: keyboard ${action}`);
    }
    if (!gamepadBindings.has(profile.gamepad[action])) {
      throw new Error(`INPUT_PROFILE_INVALID: gamepad ${action}`);
    }
  }

  assertUnique(
    gbaInputActions.map((action) => profile.keyboard[action]),
    'keyboard',
  );
  assertUnique(
    gbaInputActions.map((action) => profile.gamepad[action]),
    'gamepad',
  );

  return profile;
}

const emulatorJs423PlayerOneDefaults: Record<number, EmulatorJsControllerBinding> = {
  0: { value: 'x', value2: 'BUTTON_2' },
  1: { value: 's', value2: 'BUTTON_4' },
  2: { value: 'v', value2: 'SELECT' },
  3: { value: 'enter', value2: 'START' },
  4: { value: 'up arrow', value2: 'DPAD_UP' },
  5: { value: 'down arrow', value2: 'DPAD_DOWN' },
  6: { value: 'left arrow', value2: 'DPAD_LEFT' },
  7: { value: 'right arrow', value2: 'DPAD_RIGHT' },
  8: { value: 'z', value2: 'BUTTON_1' },
  9: { value: 'a', value2: 'BUTTON_3' },
  10: { value: 'q', value2: 'LEFT_TOP_SHOULDER' },
  11: { value: 'e', value2: 'RIGHT_TOP_SHOULDER' },
  12: { value: 'tab', value2: 'LEFT_BOTTOM_SHOULDER' },
  13: { value: 'r', value2: 'RIGHT_BOTTOM_SHOULDER' },
  14: { value: '', value2: 'LEFT_STICK' },
  15: { value: '', value2: 'RIGHT_STICK' },
  16: { value: 'h', value2: 'LEFT_STICK_X:+1' },
  17: { value: 'f', value2: 'LEFT_STICK_X:-1' },
  18: { value: 'g', value2: 'LEFT_STICK_Y:+1' },
  19: { value: 't', value2: 'LEFT_STICK_Y:-1' },
  20: { value: 'l', value2: 'RIGHT_STICK_X:+1' },
  21: { value: 'j', value2: 'RIGHT_STICK_X:-1' },
  22: { value: 'k', value2: 'RIGHT_STICK_Y:+1' },
  23: { value: 'i', value2: 'RIGHT_STICK_Y:-1' },
  24: { value: '1' },
  25: { value: '2' },
  26: { value: '3' },
  27: {},
  28: {},
  29: {},
};

export function buildEmulatorJsDefaultControllers(
  profile: GbaInputProfile,
): EmulatorJsDefaultControllers {
  validateGbaInputProfile(profile);

  const playerOne: Record<number, EmulatorJsControllerBinding> = Object.fromEntries(
    Object.entries(emulatorJs423PlayerOneDefaults).map(([id, binding]) => [
      Number(id),
      { ...binding },
    ]),
  );

  for (const action of gbaInputActions) {
    const id = gbaEmulatorJsButtonIds[action];
    playerOne[id] = {
      ...playerOne[id],
      value: profile.keyboard[action],
      value2: profile.gamepad[action],
    };
  }

  return {
    0: playerOne,
    1: {},
    2: {},
    3: {},
  };
}
